import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { FiX, FiUpload, FiCheck, FiArrowLeft, FiArrowRight, FiSearch, FiMapPin, FiShield, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import api from '../../../services/api';
import { toast } from 'react-toastify';

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
  width: 504px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    flex-shrink: 0;

    .header-left {
      display: flex;
      align-items: center;
      gap: 10px;

      h3 { font-size: 0.95em; font-weight: 600; color: ${({ theme }) => theme.text}; margin: 0; }
      .step-badge {
        font-size: 0.62em;
        padding: 2px 8px;
        border-radius: 12px;
        background: ${({ theme }) => theme.primary}22;
        color: ${({ theme }) => theme.primary};
        font-weight: 600;
      }
    }

    .close-btn {
      background: none; border: none;
      color: ${({ theme }) => theme.text}; opacity: 0.4;
      cursor: pointer; font-size: 18px;
      display: flex; padding: 4px; border-radius: 6px;
      transition: all 0.15s;
      &:hover { opacity: 1; background: ${({ theme }) => theme.border}; }
    }
  }

  .search-section {
    padding: 16px 24px 0;
    flex-shrink: 0;
  }

  .modal-body {
    flex: 1;
    overflow-y: overlay;
    padding: 12px 24px 20px;
  }

  .modal-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-top: 1px solid ${({ theme }) => theme.border};
    flex-shrink: 0;

    button {
      padding: 9px 18px;
      border-radius: 12px;
      font-size: 0.8em;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s;
      display: flex;
      align-items: center;
      gap: 6px;

      &.secondary {
        background: transparent;
        border: 1px solid ${({ theme }) => theme.border};
        color: ${({ theme }) => theme.text};
        &:hover { background: ${({ theme }) => theme.border}; }
      }

      &.primary {
        background: ${({ theme }) => theme.primary};
        border: none;
        color: #fff;
        &:hover { opacity: 0.9; }
        &:disabled { opacity: 0.35; cursor: not-allowed; }
      }
    }
  }

  /* Step 1 — Company Selection */
  .search-bar {
    position: relative;
    margin-bottom: 16px;

    svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); opacity: 0.3; }
    input {
      width: 100%;
      padding: 10px 12px 10px 36px;
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      background: ${({ theme }) => theme.body};
      color: ${({ theme }) => theme.text};
      font-size: 0.82em;
      &:focus { outline: none; border-color: ${({ theme }) => theme.primary}; }
      &::placeholder { opacity: 0.3; }
    }
  }

  .company-grid {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .company-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s;
    background: ${({ theme }) => theme.body};
    opacity: 0.7;

    &:hover { border-color: ${({ theme }) => theme.primary}; opacity: 1; }
    &.selected {
      border-color: ${({ theme }) => theme.primary};
      background: ${({ theme }) => theme.primary}0a;
      box-shadow: 0 0 0 1px ${({ theme }) => theme.primary}40;
    }

    .company-avatar {
      width: 36px; height: 36px;
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 0.72em; color: #fff;
      flex-shrink: 0;
    }

    .company-info {
      flex: 1;
      min-width: 0;

      .company-name {
        font-size: 0.82em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .company-meta {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 2px;
        font-size: 0.7em;
        opacity: 0.5;
        color: ${({ theme }) => theme.text};

        span { display: flex; align-items: center; gap: 3px; }
      }
    }

    .company-units {
      font-size: 0.65em;
      padding: 2px 8px;
      border-radius: 6px;
      background: ${({ theme }) => theme.border};
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .expand-btn {
      background: none; border: none;
      color: ${({ theme }) => theme.text};
      opacity: 0.3; cursor: pointer;
      display: flex; padding: 2px;
      &:hover { opacity: 0.7; }
    }
  }

  .filiais-list {
    padding-left: 48px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 4px;

    .filial-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px;
      border-radius: 12px;
      cursor: pointer;
      font-size: 0.76em;
      transition: all 0.15s;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      background: ${({ theme }) => theme.body};
      border: 1px solid transparent;

      &:hover { border-color: ${({ theme }) => theme.primary}; opacity: 1 !important; }
      &.selected {
        border-color: ${({ theme }) => theme.primary};
        background: ${({ theme }) => theme.primary}0a;
        opacity: 1 !important;
      }

      .filial-cnpj { font-family: monospace; font-size: 0.9em; }
      .filial-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .filial-loc { font-size: 0.85em; opacity: 0.5; display: flex; align-items: center; gap: 3px; }
    }
  }

  .no-results {
    text-align: center;
    padding: 40px 20px;
    opacity: 0.3;
    font-size: 0.85em;
  }

  /* Step 2 — Details */
  .selected-company-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 12px;
    background: ${({ theme }) => theme.primary}0a;
    border: 1px solid ${({ theme }) => theme.primary}30;
    margin-bottom: 20px;

    .company-avatar {
      width: 32px; height: 32px;
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 0.68em; color: #fff;
      flex-shrink: 0;
    }

    .banner-info {
      flex: 1;
      .banner-name { font-size: 0.82em; font-weight: 600; color: ${({ theme }) => theme.text}; }
      .banner-cnpj { font-size: 0.7em; opacity: 0.5; font-family: monospace; }
    }

    .change-btn {
      background: none; border: none;
      font-size: 0.7em; color: ${({ theme }) => theme.primary};
      cursor: pointer; font-weight: 600;
      &:hover { text-decoration: underline; }
    }
  }

  .form-group {
    margin-bottom: 16px;

    label {
      display: block;
      font-size: 0.76em;
      font-weight: 500;
      opacity: 0.6;
      margin-bottom: 6px;
    }

    input, textarea {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      background: ${({ theme }) => theme.body};
      color: ${({ theme }) => theme.text};
      font-size: 0.82em;
      font-family: inherit;
      transition: border-color 0.15s;
      &:focus { outline: none; border-color: ${({ theme }) => theme.primary}; }
      &::placeholder { opacity: 0.3; }
    }

    textarea {
      min-height: 140px;
      resize: vertical;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.75em;
      line-height: 1.6;
    }

    .input-hint { font-size: 0.68em; opacity: 0.3; margin-top: 4px; }
    .key-count { font-size: 0.72em; font-weight: 600; margin-top: 6px; color: ${({ theme }) => theme.primary}; }
  }
`;

function hashColor(str) {
  const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#ec4899','#84cc16','#f97316','#6366f1'];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

export default function NewRequestModal({ isOpen, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCnpj, setSelectedCnpj] = useState(null);
  const [expandedRoot, setExpandedRoot] = useState(null);
  const [certPassword, setCertPassword] = useState('');
  const [keysText, setKeysText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSearch('');
      setSelectedCompany(null);
      setSelectedCnpj(null);
      setExpandedRoot(null);
      setCertPassword('');
      setKeysText('');
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await api.get('/nfe-downloader/certificates');
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast.error('Erro ao buscar certificados');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return companies;
    const q = search.toLowerCase();
    return companies.filter(c =>
      (c.companyName || '').toLowerCase().includes(q) ||
      (c.cnpj || '').includes(q.replace(/\D/g, '')) ||
      (c.city || '').toLowerCase().includes(q) ||
      (c.state || '').toLowerCase().includes(q)
    );
  }, [companies, search]);

  const parseKeys = () => {
    return keysText
      .split(/[\n,;\s]+/)
      .map(k => k.replace(/\D/g, ''))
      .filter(k => k.length === 44);
  };
  const validKeyCount = parseKeys().length;

  const handleSelectCompany = (company, cnpj) => {
    setSelectedCompany(company);
    setSelectedCnpj(cnpj || company.cnpj);
  };

  const handleContinue = () => {
    if (selectedCompany && selectedCnpj) setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setCertPassword('');
    setKeysText('');
  };

  const handleSubmit = async () => {
    if (!selectedCompany || !selectedCnpj || validKeyCount === 0 || !certPassword) return;
    setSubmitting(true);
    try {
      const keys = parseKeys();
      const response = await api.post('/nfe-downloader/requests', {
        projectId: selectedCompany.projectId,
        cnpj: selectedCnpj,
        companyName: selectedCompany.companyName,
        chaves: keys,
        certPassword
      });
      if (response.data.success) {
        const d = response.data.data;
        toast.success(`Requisição criada: ${d.totalKeys} chaves (${d.alreadyDownloaded} já baixadas)`);
        onCreated && onCreated();
        onClose();
      } else {
        toast.error(response.data.message || 'Erro ao criar requisição');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar requisição');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setKeysText(ev.target.result);
    reader.readAsText(file);
  };

  const fmtCnpj = (d) => {
    if (!d || d.length !== 14) return d;
    return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-left">
            <h3>{step === 1 ? 'Selecionar Empresa' : 'Nova Requisição'}</h3>
            <span className="step-badge">Passo {step}/2</span>
          </div>
          <button className="close-btn" onClick={onClose}><FiX /></button>
        </div>

        {step === 1 && (
          <div className="search-section">
            <div className="search-bar">
              <FiSearch size={14} />
              <input
                placeholder="Buscar empresa, CNPJ ou cidade..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>
        )}

        <div className="modal-body">
          {step === 1 ? (
            <>
              {loading ? (
                <div className="no-results">Carregando certificados...</div>
              ) : filtered.length === 0 ? (
                <div className="no-results">Nenhuma empresa encontrada</div>
              ) : (
                <div className="company-grid">
                  {filtered.map(company => {
                    const color = hashColor(company.root);
                    const initials = (company.companyName || 'E').slice(0, 2).toUpperCase();
                    const isSelected = selectedCompany?.root === company.root && selectedCnpj === company.cnpj;
                    const isExpanded = expandedRoot === company.root;
                    const hasFiliais = company.filiais && company.filiais.length > 0 && company.isMatriz;

                    return (
                      <React.Fragment key={company.root}>
                        <div
                          className={`company-row ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSelectCompany(company, company.cnpj)}
                        >
                          <div className="company-avatar" style={{ background: color }}>{initials}</div>
                          <div className="company-info">
                            <div className="company-name">{company.companyName}</div>
                            <div className="company-meta">
                              <span>{fmtCnpj(company.cnpj)}</span>
                              {company.city && company.state && (
                                <span><FiMapPin size={10} /> {company.city}/{company.state}</span>
                              )}
                            </div>
                          </div>
                          {company.totalUnits > 1 && (
                            <span className="company-units">{company.totalUnits} unidades</span>
                          )}
                          {hasFiliais && (
                            <button className="expand-btn" onClick={(e) => {
                              e.stopPropagation();
                              setExpandedRoot(isExpanded ? null : company.root);
                            }}>
                              {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                            </button>
                          )}
                        </div>

                        {isExpanded && hasFiliais && (
                          <div className="filiais-list">
                            {company.filiais.map(filial => (
                              <div
                                key={filial.cnpj}
                                className={`filial-row ${selectedCnpj === filial.cnpj ? 'selected' : ''}`}
                                onClick={() => handleSelectCompany(company, filial.cnpj)}
                              >
                                <span className="filial-cnpj">{fmtCnpj(filial.cnpj)}</span>
                                <span className="filial-name">{filial.companyName}</span>
                                {filial.city && filial.state && (
                                  <span className="filial-loc"><FiMapPin size={9} /> {filial.city}/{filial.state}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="selected-company-banner">
                <div className="company-avatar" style={{ background: hashColor(selectedCompany.root) }}>
                  {(selectedCompany.companyName || 'E').slice(0, 2).toUpperCase()}
                </div>
                <div className="banner-info">
                  <div className="banner-name">{selectedCompany.companyName}</div>
                  <div className="banner-cnpj">{fmtCnpj(selectedCnpj)}</div>
                </div>
                <button className="change-btn" onClick={handleBack}>Trocar</button>
              </div>

              <div className="form-group">
                <label><FiShield size={11} style={{ marginRight: 4 }} />Senha do Certificado</label>
                <input
                  type="password"
                  placeholder="Senha do certificado .pfx"
                  value={certPassword}
                  onChange={e => setCertPassword(e.target.value)}
                  autoFocus
                />
                <div className="input-hint">A senha será criptografada com AES-256</div>
              </div>

              <div className="form-group">
                <label>
                  Chaves de Acesso
                  <span style={{ marginLeft: 8, opacity: 0.5, fontSize: '0.9em' }}>(uma por linha, 44 dígitos)</span>
                </label>
                <textarea
                  placeholder="Cole as chaves aqui..."
                  value={keysText}
                  onChange={e => setKeysText(e.target.value)}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="key-count">
                    {validKeyCount > 0 ? `${validKeyCount} chave(s) válida(s)` : ''}
                  </div>
                  <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72em', opacity: 0.5, padding: '4px 0' }}>
                    <FiUpload size={12} />
                    Importar .txt
                    <input type="file" accept=".txt,.csv" style={{ display: 'none' }} onChange={handleFileUpload} />
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {step === 1 ? (
            <>
              <button className="secondary" onClick={onClose}>Cancelar</button>
              <button className="primary" disabled={!selectedCompany || !selectedCnpj} onClick={handleContinue}>
                Continuar <FiArrowRight size={14} />
              </button>
            </>
          ) : (
            <>
              <button className="secondary" onClick={handleBack}>
                <FiArrowLeft size={14} /> Voltar
              </button>
              <button
                className="primary"
                disabled={!certPassword || validKeyCount === 0 || submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Criando...' : <><FiCheck size={14} /> Criar Requisição ({validKeyCount})</>}
              </button>
            </>
          )}
        </div>
      </Modal>
    </Overlay>
  );
}
